import React from 'react';

/**
 * Fixed banner pinned to the top-centre of the screen, for transient status
 * that must not be missed (backend unreachable, batch operation failed…).
 *
 * The message is a prop so one banner style covers every case. Width is capped
 * and text wraps and centres, so a long message stays on screen instead of
 * running off the edge — the original hard-coded banner shrink-wrapped to its
 * content, which only worked because its message was short.
 *
 * Render conditionally from the parent, matching existing usage:
 *   {serverUnreachable && <StatusBanner message="…" />}
 *
 * NOTE: two visible banners would overlap, since both sit at top: 0. If a
 * second concurrent status is ever needed, give this an offset/stacking prop
 * rather than adding a second fixed element.
 */

export type StatusBannerTone = 'error' | 'info';

type StatusBannerProps = {
  message: string;
  tone?: StatusBannerTone;
};

const TONE_BACKGROUNDS: Record<StatusBannerTone, string> = {
  error: 'rgba(180, 60, 60, 0.9)',
  info: 'rgba(60, 110, 180, 0.9)',
};

const StatusBanner: React.FC<StatusBannerProps> = ({ message, tone = 'error' }) => {
  if (!message) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      backgroundColor: TONE_BACKGROUNDS[tone],
      color: 'white',
      padding: '0.3dvw 1.2dvw',
      borderRadius: '0 0 0.5dvw 0.5dvw',
      fontSize: '1dvw',
      // Cap the width so long messages wrap instead of running off screen.
      maxWidth: '60dvw',
      textAlign: 'center',
    }}>
      {message}
    </div>
  );
};

export default StatusBanner;
